import { TASK_TIMEOUT } from '../../../../../../app/package/base/service/render/render-service';
import { tick } from '../../../util/render';

/**
 * Available feature flags.
 * To be used in the app.
 */
export const FeatureFlag = {
  A11yWidgetEnabled: 'a11yWidgetEnabled', // ABTasty feature: a11yWidgetEnabled=boolean
  StoriesEnabled: 'storiesEnabled', // ABTasty feature: ???=boolean
  BestMatchEnabled: 'bestMatchEnabled', // ABTasty feature: ???=boolean
} as const;

/**
 * ABTasty feature flags.
 * To be used in local storage.
 * Set by ABTasty or manually.
 */
const ABTastyFeatureFlags = {
  a11yWidgetEnabled: 'a11yWidgetEnabled',
  storiesEnabled: 'storiesEnabled',
  bestMatchEnabled: 'bestMatchEnabled',
} as const;

export type FeatureMap = Map<string, boolean | string>;
export type Features = { [key: string]: FeatureValue };
export type FeatureValue = boolean | string;
type FeaturesABTasty = { [key: string]: string };

export interface IFeatureService {
  getFeature(feature: string): Promise<FeatureValue | undefined>;
  hasFeature(feature: string): Promise<boolean>;
  setFeature(feature: string, value: string): void;
}

/**
 * Feature service to check if a feature is enabled.
 * Prioritizes stored features over global values.
 * Stored features are managed by ABTasty.
 * Global values are managed by the app.
 *
 * Important: The local storage values are read from local storage and mapped to internal features once when a feature is checked.
 */
export class FeatureService implements IFeatureService {
  private _initializedFromLocalStorage = false;
  private _initializing = false;
  private readonly _localStorageABTastyKey = 'feature'; // managed by ABTasty without app prefix
  private readonly _storedFeatures: FeatureMap = new Map<string, boolean | string>();
  private _abReadyResolve: () => void;
  private _abReadyPromise: Promise<void> = new Promise((resolve) => {
    this._abReadyResolve = resolve;
  });

  public setFeature(feature: string, value: string): void {
    window.$app.logger.log(`setFeature: ${feature}: '${value}'`);
    this._updateABTastyFeature(feature, value);
  }

  /**
   * Check if a feature is enabled.
   * Prioritizes stored features over global values.
   * @param feature - The feature to check.
   * @param ignoreOverride - If true, the override value will be ignored.
   * @returns A promise that resolves to true if the feature is enabled, otherwise false.
   */
  public async hasFeature(feature: string, ignoreOverride = false): Promise<boolean> {
    if (this._initializing) {
      window.$app.logger.log('hasFeature: await _pauseUntilAbTastyIsReady()');
      await this._pauseUntilAbTastyIsReady();
      await tick(); // wait for next tick to be sure that the features are picked up and set
    }
    if (!this._initializedFromLocalStorage) {
      this._initializing = true;
      window.$app.logger.log('hasFeature: await _initializeFromLocalStorage()');
      await this._initializeFromLocalStorage();
      this._initializedFromLocalStorage = true;
    }
    const storedFeature = this._storedFeatures.has(feature) ? this._storedFeatures.get(feature) : undefined;
    const globalFeature = window.$featureFlags[feature];
    let value = storedFeature ?? globalFeature;

    const override = await this._getOverrideValue(feature);
    if (!ignoreOverride && override !== undefined) {
      value = override;
    }

    const isEnabled = Boolean(value);
    window.$app.logger.log(`hasFeature: '${feature}', enabled: ${isEnabled} (ignoreOverride: ${ignoreOverride})`);
    return isEnabled;
  }

  /**
   * Get value for the feature.
   * Prioritizes stored features over global values.
   * @param feature - The feature to check.
   * @param ignoreOverride - If true, the override value will be ignored.
   * @returns A promise that resolves with the requested feature's value, or if absent, undefined.
   */
  public async getFeature(feature: string, ignoreOverride = false): Promise<FeatureValue | undefined> {
    if (this._initializing) {
      window.$app.logger.log('getFeature: await _pauseUntilAbTastyIsReady()');
      await this._pauseUntilAbTastyIsReady();
      await tick(); // wait for next tick to be sure that the features are picked up and set
    }
    if (!this._initializedFromLocalStorage) {
      this._initializing = true;
      window.$app.logger.log('getFeature: await _initializeFromLocalStorage()');
      await this._initializeFromLocalStorage();
      this._initializedFromLocalStorage = true;
    }
    const storedFeature = this._storedFeatures.has(feature) ? this._storedFeatures.get(feature) : undefined;
    const globalFeature = window.$featureFlags[feature];
    let value = storedFeature ?? globalFeature;

    const override = await this._getOverrideValue(feature);
    if (!ignoreOverride && override !== undefined) {
      value = override;
    }

    window.$app.logger.log(`getFeature: '${feature}', value: ${value} (ignoreOverride: ${ignoreOverride})`);
    return value;
  }

  /**
   * Called when feature flags are ready in local storage (aka ABTasty has done its job).
   */
  public featureFlagsReady(): void {
    window.$app.logger.log('feature flags ready');
    tick() // Wait for next tick in case the feature flags are set in the same tick.
      .then(() => {
        this._abReadyResolve();
        this._abReadyPromise = Promise.resolve();

        if (this._initializedFromLocalStorage) {
          // Re-Initialize from local storage if already done
          this._initializeFromLocalStorage();
        }
      });
  }

  /**
   * Get the override value for a feature.
   * These values are set by the dev panel.
   */
  private async _getOverrideValue(feature: string): Promise<FeatureValue | undefined> {
    if (window.$app.dev) {
      const override = await window.$app.dev.getOverrideValue?.(feature);
      if (override !== undefined) {
        window.$app.logger.log(`getOverrideValue: '${feature}', value: ${override}`);
        return override;
      }
    }
    return undefined;
  }

  /**
   * Pause until ABTasty is ready.
   * If ABTasty is not ready after 3 seconds, the promise will be automatically resolved.
   * @returns A promise that resolves when ABTasty is ready.
   */
  private _pauseUntilAbTastyIsReady(): Promise<void> {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        window.$app.logger.warn('ABTasty did not call abReady() within 3 seconds');
        resolve(); // Resolve even on timeout
      }, TASK_TIMEOUT); // Keep in sync with render service timeout!

      this._abReadyPromise.then(() => {
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }

  /**
   * Initialize the feature service from local storage.
   * Be sure to call this method only once when ABTasty has set the features.
   */
  private async _initializeFromLocalStorage(): Promise<void> {
    await this._pauseUntilAbTastyIsReady();
    const features = this._getABTastyFeatures();
    window.$app.logger.log('ABTasty features', features);
    Object.entries(features).forEach(([key, value]) => {
      this._storedFeatures.set(key, value);
    });
  }

  /**
   * Map ABTasty features to internal features.
   */
  private _mapABTastyFeatures(features: FeaturesABTasty): Features {
    const mappedFeatures: Features = {};
    // map ABTasty features to internal features
    Object.entries(features).forEach(([key, value]) => {
      if (key === ABTastyFeatureFlags.a11yWidgetEnabled) {
        mappedFeatures[FeatureFlag.A11yWidgetEnabled] = value;
      }
      if (key === ABTastyFeatureFlags.storiesEnabled) {
        mappedFeatures[FeatureFlag.StoriesEnabled] = value;
      }
      if (key === ABTastyFeatureFlags.bestMatchEnabled) {
        mappedFeatures[FeatureFlag.BestMatchEnabled] = value;
      }
    });
    return mappedFeatures;
  }

  /**
   * Get ABTasty features from local storage.
   */
  private _getABTastyFeatures(): Features {
    try {
      const features: FeaturesABTasty = JSON.parse(localStorage.getItem(this._localStorageABTastyKey) ?? '{}');
      return this._mapABTastyFeatures(features);
    } catch (err) {
      window.$app.logger.error('can not parse feature object (of ABTasty) from local storage', err);
      return {};
    }
  }

  /**
   * Update ABTasty features in local storage.
   */
  private _updateABTastyFeature(feature: string, value: string): void {
    try {
      // Update internal features
      this._storedFeatures.set(feature, value);

      // Update ABTasty features in local storage
      const features: FeaturesABTasty = JSON.parse(localStorage.getItem(this._localStorageABTastyKey) ?? '{}');
      features[feature] = value;
      localStorage.setItem(this._localStorageABTastyKey, JSON.stringify(features));
    } catch (err) {
      window.$app.logger.error('can not update feature object (of ABTasty) in local storage', err);
    }
  }
}
